import { CONSENT_TYPE_CATEGORY, CONSENT_TYPE_GRANULAR, ConsentTimestamp, ConsentType, IConsent } from "types/consent";

import { CONSENT_COOKIE, WITHDRAW_COOKIE } from "core/config";
import { NULL } from "constants/primitives";
import { consentLogger } from "helpers/logger";
import { genericObject } from "types/generic";
import getCookie from "helpers/getCookie";
import getLocalStorageItem from "helpers/getLocalStorageItem";
import objectKeys from "helpers/objectKeys";
import report from "helpers/report";
import includes from "helpers/includes";
import deleteLocalStorageItem from "helpers/deleteLocalStorageItem";

const loadLocalConsentCookie = (): genericObject | null => {
  const cookie = getCookie(CONSENT_COOKIE);
  // cookie doesn't exist, no consent
  if (!cookie) {
    return NULL;
  }

  // Check if cookie is equal to "withdraw" value
  if (cookie === WITHDRAW_COOKIE) {
    return { value: cookie }
  }

  try {
    const decoded: genericObject = JSON.parse(decodeURIComponent(cookie));

    return decoded;
  } catch (e) {
    consentLogger("Failed to decode local consent cookie:");
    report(e);
    // not valid JSON value in cookie
    return NULL;
  }
};

const formatStoredValue = (
  storedValue: genericObject
): [IConsent, ConsentTimestamp, ConsentType] => {
  const consent = objectKeys(storedValue).reduce(
    (consentObj: genericObject, key: keyof typeof storedValue) => {
      // don't pass a few keys to the final consent object
      if (includes(["version", "timestamp", "type"], key)) {
        return consentObj;
      }

      return { ...consentObj, [key]: !!storedValue[key] };
    },
    {}
  );

  let type: ConsentType = CONSENT_TYPE_CATEGORY;
  if (includes(
    [CONSENT_TYPE_CATEGORY, CONSENT_TYPE_GRANULAR],
    storedValue.type || ""
  )
  ) {
    type = storedValue.type;
  }

  return [
    { ...consent, version: storedValue.version },
    parseInt(storedValue.timestamp || "0"), // use numeric timestamp here,
    type,
  ];
};

const loadLocalConsent = (): LoadLocalConsentResult => {
  // try to load consent from cookie
  const cookie = loadLocalConsentCookie();

  if (cookie) {
    // Check if value of cookie is 'withdraw'
    if (cookie.value === WITHDRAW_COOKIE) {
      // If so, doesn't return any consent and clear local storage consent.
      deleteLocalStorageItem(CONSENT_COOKIE);
      return NULL;
    }

    return formatStoredValue(cookie);
  }

  // fall back to localstorage if cookie is not available
  const localStorageItem = getLocalStorageItem<genericObject, null>(
    CONSENT_COOKIE,
    NULL
  );
  if (localStorageItem) {
    return formatStoredValue(localStorageItem);
  }

  // no consent
  return NULL;
};

export type LoadLocalConsentResult =
  | [IConsent, ConsentTimestamp, ConsentType]
  | null;

export default loadLocalConsent;
